import React, { useState } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { MessageCircle, CheckCircle2, Send, Search, MapPin, Building2, ChevronDown, ArrowRight } from 'lucide-react';
import { WA_URL } from '../lib/utils';
import { SPECIALTIES } from '../data/mock';
import { findCity, PROCESS_STEPS, POSITIONS, DIFERENCIAIS, LOCAL_REGION } from '../data/localSeo';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import Reveal from '../components/Reveal';
import SeoInternalLinks from '../components/SeoInternalLinks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = process.env.REACT_APP_BACKEND_URL;

const LocalSeoPage = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  const data = findCity(slug);
  const { toast } = useToast();

  const [form, setForm] = useState({
    company: '', cnpj: '', contact_name: '', email: '', phone: '',
    position: '', area: '', location: data ? `${data.city}, ${data.uf}` : '',
    vacancies: '1', modality: 'Presencial', salary: '', description: '',
  });
  const [sending, setSending] = useState(false);

  if (!data) return <Navigate to="/para-empresas" replace />;

  const cityLabel = data.shortCity || data.city;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/proposals`, { ...form, source: `SEO Local - ${data.city}` });
      toast({ title: 'Solicitação enviada!', description: `Recebemos sua proposta para ${data.city} e entraremos em contato em breve.` });
      setForm({ company: '', cnpj: '', contact_name: '', email: '', phone: '', position: '', area: '', location: `${data.city}, ${data.uf}`, modality: 'Presencial', vacancies: '1', salary: '', description: '' });
    } catch (err) {
      toast({ title: 'Erro ao enviar', description: 'Tente novamente ou fale conosco pelo WhatsApp.', variant: 'destructive' });
    }
    setSending(false);
  };

  const waHero = WA_URL(`Olá! Sou de ${data.city} e gostaria de saber mais sobre recrutamento e seleção da HM Talent.`);

  const sendViaWhatsApp = () => {
    axios.post(`${API}/proposals`, { ...form, source: `SEO Local - ${data.city} (via WhatsApp)` }).catch(() => {});
    const msg = `🟧 *Solicitação de Proposta - HM Talent*
_Lead da página de ${data.city}/${data.uf}_

*Empresa:* ${form.company}
*CNPJ:* ${form.cnpj}
*Contato:* ${form.contact_name}
*E-mail:* ${form.email}
*Telefone:* ${form.phone}

*Cargo:* ${form.position}
*Área:* ${form.area}
*Localização:* ${form.location}
*Modalidade:* ${form.modality}
*Vagas:* ${form.vacancies}
*Salário:* ${form.salary}

*Descrição/Requisitos:*
${form.description}`;
    window.open(WA_URL(msg), '_blank');
  };

  const canonical = `${SITE}/${data.slug}`;
  const pageTitle = `${data.title} | HM Talent`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ProfessionalService',
        '@id': `${canonical}#service`,
        name: `HM Talent — Recrutamento e Seleção em ${data.city}`,
        description: data.metaDescription,
        url: canonical,
        telephone: '+55-11-92089-7014',
        email: 'higor@hmtalent.com.br',
        areaServed: { '@type': 'City', name: `${data.city}, ${data.uf}` },
        address: { '@type': 'PostalAddress', addressLocality: 'Mauá', addressRegion: 'SP', addressCountry: 'BR' },
        parentOrganization: { '@type': 'Organization', name: 'HM Talent' },
        makesOffer: {
          '@type': 'Offer',
          itemOffered: { '@type': 'Service', name: `Recrutamento e Seleção em ${data.city}`, serviceType: 'Recrutamento e Seleção' },
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: data.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <div data-testid={`local-seo-${data.slug}`}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={data.metaDescription} />
        <meta name="keywords" content={data.keywords.join(', ')} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={data.title} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content="pt_BR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={data.title} />
        <meta name="twitter:description" content={data.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="hm-gradient text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm px-6 max-w-4xl relative">
          <Reveal>
            <p className="text-sm uppercase tracking-widest text-white/85 font-bold mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> {data.city} • {LOCAL_REGION}
            </p>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">{data.title}</h1>
            <p className="mt-4 text-lg text-white/95 max-w-2xl">{data.heroSubtitle}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a href="#solicitar-proposta" data-testid="local-hero-proposta" className="btn-hm-white"><Send className="w-5 h-5" /> Solicitar Proposta</a>
              <a href={waHero} target="_blank" rel="noopener noreferrer" data-testid="local-hero-whatsapp" className="btn-hm-ghost"><MessageCircle className="w-5 h-5" /> Falar no WhatsApp</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTRO + SEGMENTOS */}
      <section className="section-padding bg-white">
        <div className="container-hm grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="slideRight">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl hm-gradient flex items-center justify-center"><Search className="w-6 h-6 text-white" /></div>
              <span className="text-xs uppercase tracking-widest text-hm-orange font-bold">Recrutamento e Seleção em {cityLabel}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Consultoria de RH que conhece {cityLabel}</h2>
            {data.intro.map((p, i) => (
              <p key={i} className="mt-4 text-slate-700 leading-relaxed">{p}</p>
            ))}
            <ul className="mt-5 space-y-2.5">
              {['Atendimento presencial no ABC e 100% online', 'Entrevistas técnicas e comportamentais', 'SLA de até 72h para as primeiras indicações', 'Garantia contratual de reposição'].map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="w-5 h-5 text-hm-orange shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal variant="slideLeft" className="hm-gradient-warm text-white rounded-3xl p-8 card-shadow">
            <Building2 className="w-12 h-12" />
            <h3 className="text-2xl font-extrabold mt-4">Segmentos que atendemos em {cityLabel}</h3>
            <p className="text-white/95 mt-2">Nossa atuação acompanha a vocação econômica da cidade.</p>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm font-semibold">
              {data.segments.map((s, i) => (
                <div key={i} className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-3 py-2.5 text-center">{s}</div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ECONOMIA LOCAL + POSIÇÕES */}
      <section className="section-padding bg-hm-yellow">
        <div className="container-hm grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="slideRight" className="lg:order-2">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Mercado de trabalho local</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Por que contratar com quem entende {cityLabel}</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">{data.economy}</p>
            <p className="mt-4 text-slate-700 leading-relaxed">Com sede em Mauá, a HM Talent está no centro do {LOCAL_REGION} e mantém uma base regional de talentos — o que aproxima empresa e candidato, reduz o tempo de contratação e diminui a rotatividade.</p>
          </Reveal>
          <Reveal variant="slideLeft" className="lg:order-1 bg-white rounded-3xl p-8 card-shadow border border-amber-100">
            <h3 className="text-2xl font-extrabold text-slate-900">Posições que recrutamos</h3>
            <p className="text-slate-600 mt-2 text-sm">De operacionais a liderança, com profundidade na avaliação.</p>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm font-semibold">
              {POSITIONS.map((p, i) => (
                <div key={i} className="hm-gradient text-white rounded-xl px-3 py-2.5 text-center">{p}</div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESSO */}
      <section className="section-padding bg-white">
        <div className="container-hm">
          <Reveal className="max-w-2xl mb-12">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Como trabalhamos</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Nosso processo de recrutamento em {cityLabel}</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 80} variant="fadeUp" className="p-7 rounded-2xl card-shadow bg-hm-yellow border border-amber-100 hover-lift">
                <span className="text-4xl font-extrabold text-hm-orange/30">{s.n}</span>
                <h3 className="text-lg font-extrabold text-slate-900 mt-2">{s.title}</h3>
                <p className="text-sm mt-2 leading-relaxed text-slate-700">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section className="section-padding bg-hm-yellow">
        <div className="container-hm">
          <Reveal className="max-w-2xl mb-12">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Diferenciais</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Por que a HM Talent</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {DIFERENCIAIS.map((d, i) => (
              <Reveal key={i} delay={i * 70} variant="fadeUp" className={`p-6 rounded-2xl hover-lift card-shadow text-center ${i === 2 ? 'hm-gradient text-white' : 'bg-white border border-amber-100'}`}>
                <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3 ${i === 2 ? 'bg-white/20' : 'hm-gradient'}`}>
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
                <h3 className={`text-base font-extrabold ${i === 2 ? 'text-white' : 'text-slate-900'}`}>{d.title}</h3>
                <p className={`text-xs mt-2 leading-relaxed ${i === 2 ? 'text-white/95' : 'text-slate-700'}`}>{d.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-white">
        <div className="container-hm max-w-3xl">
          <Reveal className="mb-10">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Perguntas frequentes</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Recrutamento e seleção em {cityLabel}: dúvidas comuns</h2>
          </Reveal>
          <div className="space-y-3" data-testid="local-faq">
            {data.faqs.map((f, i) => (
              <details key={i} className="group bg-hm-yellow border border-amber-100 rounded-2xl px-5 py-4 [&_svg]:open:rotate-180">
                <summary className="flex items-center justify-between cursor-pointer list-none font-bold text-slate-900">
                  {f.q}
                  <ChevronDown className="w-5 h-5 text-hm-orange shrink-0 transition-transform" />
                </summary>
                <p className="mt-3 text-slate-700 leading-relaxed text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* LINKS INTERNOS */}
      <SeoInternalLinks currentSlug={data.slug} />

      {/* FORMULÁRIO SOLICITAR PROPOSTA */}
      <section id="solicitar-proposta" className="section-padding bg-hm-yellow">
        <div className="container-hm grid lg:grid-cols-3 gap-8">
          <Reveal variant="slideRight" className="lg:col-span-1">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Solicite uma proposta</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Precisa contratar em {cityLabel}?</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">Preencha o formulário e nossa equipe entrará em contato em até 24h úteis com uma proposta personalizada para sua vaga em {data.city}.</p>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-hm-orange" /> Resposta em até 24h úteis</div>
              <div className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-hm-orange" /> Sem compromisso inicial</div>
              <div className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-hm-orange" /> Proposta personalizada</div>
            </div>
            <a href={waHero} target="_blank" rel="noopener noreferrer" data-testid="local-form-whatsapp-link" className="mt-6 inline-flex items-center gap-2 text-hm-orange font-semibold hover:underline">
              <MessageCircle className="w-4 h-4" /> Ou fale direto no WhatsApp
            </a>
          </Reveal>
          <Reveal variant="slideLeft" className="lg:col-span-2 bg-white border border-amber-200 rounded-3xl p-7 card-shadow">
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
              <div><label className="text-sm font-semibold text-slate-700">Nome da empresa *</label><Input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 h-11" /></div>
              <div><label className="text-sm font-semibold text-slate-700">CNPJ</label><Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} className="mt-1 h-11" placeholder="00.000.000/0000-00" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Nome do responsável *</label><Input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1 h-11" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Cargo</label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="mt-1 h-11" placeholder="Gerente de RH" /></div>
              <div><label className="text-sm font-semibold text-slate-700">E-mail *</label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Telefone/WhatsApp *</label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11" placeholder="(11) 99999-9999" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Área da vaga *</label>
                <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-semibold text-slate-700">Cidade/UF</label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 h-11" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Modalidade</label>
                <Select value={form.modality} onValueChange={(v) => setForm({ ...form, modality: v })}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Presencial">Presencial</SelectItem><SelectItem value="Híbrido">Híbrido</SelectItem><SelectItem value="Remoto">Remoto</SelectItem></SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-semibold text-slate-700">Quantidade de vagas</label><Input type="number" min="1" value={form.vacancies} onChange={(e) => setForm({ ...form, vacancies: e.target.value })} className="mt-1 h-11" /></div>
              <div className="md:col-span-2"><label className="text-sm font-semibold text-slate-700">Faixa salarial</label><Input value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} className="mt-1 h-11" placeholder="R$ 5.000 - R$ 7.000 (ou 'a combinar')" /></div>
              <div className="md:col-span-2"><label className="text-sm font-semibold text-slate-700">Descrição da vaga e principais requisitos *</label><Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} className="mt-1" placeholder="Descreva o cargo, responsabilidades, requisitos técnicos, formação desejada..." /></div>
              <div className="md:col-span-2 flex flex-col sm:flex-row justify-end gap-3">
                <button type="button" onClick={sendViaWhatsApp} data-testid="local-proposal-whatsapp-btn" className="btn-hm-outline"><MessageCircle className="w-4 h-4" /> Enviar pelo WhatsApp</button>
                <button type="submit" disabled={sending} data-testid="local-proposal-submit-btn" className="btn-hm disabled:opacity-60">
                  {sending ? 'Enviando...' : <>Solicitar Proposta <Send className="w-4 h-4" /></>}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {/* CHAMADA FINAL */}
      <section className="section-padding hm-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm text-center relative">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-extrabold">Contrate os melhores talentos de {cityLabel}</h2>
            <p className="mt-4 text-white/95 max-w-2xl mx-auto text-lg">Fale com a HM Talent e receba uma proposta personalizada para o seu processo seletivo.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a href={waHero} target="_blank" rel="noopener noreferrer" data-testid="local-final-whatsapp" className="btn-hm-white"><MessageCircle className="w-5 h-5" /> Falar no WhatsApp</a>
              <a href="#solicitar-proposta" className="btn-hm-ghost"><Send className="w-5 h-5" /> Solicitar Proposta</a>
            </div>
            <p className="mt-8 text-sm text-white/80">
              <Link to="/para-empresas" className="underline hover:text-white">Conheça todos os serviços para empresas da HM Talent</Link>
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default LocalSeoPage;
